import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import log from 'loglevel';

import { useServices } from '@/components/ServiceContext';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import { ExclusionRule } from '@/API';
import { useMessageQueue } from '@erickson/components';
import { houseListFilters } from '@/pages/HouseListFilterPage';
import { boughtListFilters } from '@/pages/BoughtListFilterPage';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { ControlType } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';

const DEFAULT_EXCLUSION_OPERATOR = 'NOT_IN' as const;

const LIST_TYPE_OPTIONS = [
  { value: 'houselist', label: 'House List' },
  { value: 'boughtlist', label: 'Bought List' },
] as const;

const EXCLUSION_SUPPORTED_CONTROL_TYPES = [
  ControlType.SELECT,
  ControlType.MULTI_SELECT,
  ControlType.SEARCHABLE_MULTI_SELECT,
] as const;

interface ExclusionRuleFormData {
  listType: string;
  fieldName: string;
  excludedValues: string[];
}

const ExclusionRulesGrid: React.FC = () => {
  const { exclusionRuleService, houseListService, boughtListService } =
    useServices();
  const messageQueue = useRef(useMessageQueue()).current;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ExclusionRule | null>(null);
  const [formData, setFormData] = useState<ExclusionRuleFormData>({
    listType: '',
    fieldName: '',
    excludedValues: [],
  });
  const [availableFieldValues, setAvailableFieldValues] = useState<string[]>(
    []
  );
  const [loadingFieldValues, setLoadingFieldValues] = useState(false);

  // Get available field names based on selected list type
  const getAvailableFieldNames = useCallback((listType: string) => {
    const getFilteredFields = (filters: FilterDefinition[]) => {
      return filters
        .filter(filter => {
          // Only include fields with labels and that are selection-based controls
          return (
            filter.label &&
            filter.csvMetadata?.fieldType === 'database' &&
            filter.controlType &&
            EXCLUSION_SUPPORTED_CONTROL_TYPES.includes(
              filter.controlType as (typeof EXCLUSION_SUPPORTED_CONTROL_TYPES)[number]
            )
          );
        })
        .map(filter => ({
          value: filter.databaseMetadata?.columnName || filter.label,
          label: filter.label,
        }))
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
    };

    if (listType === 'houselist') {
      return getFilteredFields(houseListFilters);
    } else if (listType === 'boughtlist') {
      return getFilteredFields(boughtListFilters);
    }
    return [];
  }, []);

  const availableFieldNames = useMemo(() => {
    return getAvailableFieldNames(formData.listType);
  }, [formData.listType, getAvailableFieldNames]);

  /**
   * Reset form data to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({
      listType: '',
      fieldName: '',
      excludedValues: [],
    });
    setEditingRule(null);
  }, []);

  /**
   * Open modal for creating a new exclusion rule
   */
  const handleAddExclusion = useCallback(() => {
    resetForm();
    setIsModalOpen(true);
  }, [resetForm]);

  /**
   * Handle deleting an exclusion rule
   */
  const handleDeleteClick = useCallback(
    (id: GridRowId) => async () => {
      try {
        await exclusionRuleService.delete(id.toString());
        messageQueue.addMessage({
          text: 'Exclusion rule deleted successfully',
          severity: 'success',
        });
        // ServiceDataGrid will automatically refresh the data
      } catch (err) {
        log.error('Error deleting exclusion rule', err);
        messageQueue.addMessage({
          text: 'Error deleting exclusion rule',
          severity: 'error',
        });
      }
    },
    [exclusionRuleService, messageQueue]
  );

  /**
   * Close modal without saving
   */
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  /**
   * Save the exclusion rule (create or update)
   */
  const handleSave = useCallback(async () => {
    // Validate form data before saving
    if (
      !formData.listType ||
      !formData.fieldName ||
      formData.excludedValues.length === 0
    ) {
      messageQueue.addMessage({
        text: 'Please fill in all required fields and add at least one excluded value',
        severity: 'warning',
      });
      return;
    }

    try {
      // Add default operator for API compatibility
      const dataToSave = {
        ...formData,
        operator: DEFAULT_EXCLUSION_OPERATOR, // Default operator for all exclusions
      };

      if (editingRule) {
        // Update existing rule - only pass allowed fields for UpdateExclusionRuleInput
        await exclusionRuleService.update({
          id: editingRule.id,
          ...dataToSave,
        });
        messageQueue.addMessage({
          text: `Exclusion rule for ${formData.fieldName} updated successfully`,
          severity: 'success',
        });
      } else {
        // Create new rule
        await exclusionRuleService.save(dataToSave);
        messageQueue.addMessage({
          text: `Exclusion rule for ${formData.fieldName} created successfully`,
          severity: 'success',
        });
      }
      setIsModalOpen(false);
      resetForm();
      // ServiceDataGrid will automatically refresh the data
    } catch (error) {
      log.error('Error saving exclusion rule', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      messageQueue.addMessage({
        text: `Error saving exclusion rule: ${errorMessage}`,
        severity: 'error',
      });
    }
  }, [editingRule, formData, exclusionRuleService, resetForm, messageQueue]);

  /**
   * Update form field values
   */
  const handleFieldChange = useCallback(
    (field: keyof ExclusionRuleFormData, value: string | string[]) => {
      setFormData(prev => {
        const updated = { ...prev, [field]: value };
        // Reset field name when list type changes
        if (field === 'listType' && prev.listType !== value) {
          updated.fieldName = '';
        }
        return updated;
      });
    },
    []
  );

  /**
   * Rendering "Excluded Values" as chips in the grid
   */
  const renderExcludedValuesCell = useCallback(
    (params: GridRenderCellParams<ExclusionRule, string[]>) => {
      const values = params.value || [];

      if (!values.length) {
        return null;
      }

      return (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ padding: 1.75 }}
        >
          {values.map((val: string) => (
            <Chip key={val} label={val} size="small" />
          ))}
        </Stack>
      );
    },
    []
  );

  const columns: GridColDef<ExclusionRule>[] = useMemo(
    () => [
      {
        field: 'listType',
        headerName: 'List Type',
        width: 150,
        editable: false,
        valueGetter: listType => {
          return LIST_TYPE_OPTIONS.find(option => option.value === listType)
            ?.label;
        },
      },
      {
        field: 'fieldName',
        headerName: 'Field Name',
        width: 150,
        editable: false,
      },
      {
        field: 'excludedValues',
        headerName: 'Excluded Values',
        width: 300,
        editable: false,
        renderCell: renderExcludedValuesCell,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 120,
        getActions: params => {
          const { id, row } = params;
          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Edit"
              onClick={() => {
                setEditingRule(row);
                setFormData({
                  listType: row.listType,
                  fieldName: row.fieldName,
                  excludedValues: row.excludedValues || [],
                });
                setIsModalOpen(true);
              }}
              color="inherit"
            />,
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [renderExcludedValuesCell, handleDeleteClick]
  );

  // Get the appropriate list service based on list type
  const getListService = useCallback(
    (listType: string) => {
      if (listType === 'houselist') {
        return houseListService;
      } else if (listType === 'boughtlist') {
        return boughtListService;
      }
      return null;
    },
    [houseListService, boughtListService]
  );

  // Get filter definitions based on list type
  const getFilterDefinitions = useCallback((listType: string) => {
    if (listType === 'houselist') {
      return houseListFilters;
    } else if (listType === 'boughtlist') {
      return boughtListFilters;
    }
    return [];
  }, []);

  // Fetch unique field values when field name changes
  useEffect(() => {
    const fetchFieldValues = async () => {
      if (!formData.listType || !formData.fieldName) {
        setAvailableFieldValues([]);
        return;
      }

      const listService = getListService(formData.listType);
      const filterDefinitions = getFilterDefinitions(formData.listType);

      if (!listService) {
        setAvailableFieldValues([]);
        return;
      }

      // Find the filter definition for the selected field
      const fieldFilter = filterDefinitions.find(
        filter =>
          filter.databaseMetadata?.columnName === formData.fieldName ||
          filter.label === formData.fieldName
      );

      if (!fieldFilter || fieldFilter.csvMetadata?.fieldType !== 'database') {
        setAvailableFieldValues([]);
        return;
      }

      try {
        setLoadingFieldValues(true);

        // Use the existing getFilterCountMap method to fetch unique values
        const countMap = await listService.getFilterCountMap(
          fieldFilter.id,
          filterDefinitions,
          {} // Empty filter selections to get all possible values
        );

        // Extract the keys (unique values) and filter out null/undefined
        const uniqueValues = Array.from(countMap.keys())
          .filter(
            (value): value is string => value !== null && value !== undefined
          )
          .sort();

        setAvailableFieldValues(uniqueValues);
      } catch (error) {
        log.error('Error fetching field values:', error);
        setAvailableFieldValues([]);
      } finally {
        setLoadingFieldValues(false);
      }
    };

    void fetchFieldValues();
  }, [
    formData.listType,
    formData.fieldName,
    getListService,
    getFilterDefinitions,
  ]);

  // Reset available field values when field name is cleared
  useEffect(() => {
    if (!formData.fieldName) {
      setAvailableFieldValues([]);
    }
  }, [formData.fieldName]);

  return (
    <Box mt={4}>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddExclusion}
        sx={{ mb: 2 }}
      >
        Add Exclusion
      </Button>
      <ServiceDataGrid
        rowId="id"
        service={exclusionRuleService}
        columns={columns}
        rowSelection={false}
        pageSize={10}
        showMultiFilterBuilder={false}
        disableColumnFilter={true}
      />

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? 'Edit Exclusion Rule' : 'Create Exclusion Rule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>List Type</InputLabel>
              <Select
                value={formData.listType}
                label="List Type"
                onChange={e =>
                  handleFieldChange('listType', e.target.value as string)
                }
              >
                {LIST_TYPE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Field Name</InputLabel>
              <Select
                value={formData.fieldName}
                label="Field Name"
                onChange={e =>
                  handleFieldChange('fieldName', e.target.value as string)
                }
              >
                {availableFieldNames.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Excluded Values
                {loadingFieldValues && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ ml: 1 }}
                  >
                    (Loading available values...)
                  </Typography>
                )}
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                options={availableFieldValues}
                value={formData.excludedValues}
                loading={loadingFieldValues}
                onChange={(_event, newValues) =>
                  handleFieldChange('excludedValues', newValues)
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={
                      formData.fieldName
                        ? availableFieldValues.length > 0
                          ? 'Select from available values or type custom values'
                          : 'Type values and press Enter'
                        : 'Select a field first'
                    }
                    helperText={
                      formData.fieldName && availableFieldValues.length > 0
                        ? `${availableFieldValues.length} unique values available for selection`
                        : 'Type a value and press Enter to add it'
                    }
                    disabled={!formData.fieldName}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        {...tagProps}
                        size="small"
                      />
                    );
                  })
                }
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    {option}
                  </li>
                )}
                getOptionLabel={option => String(option)}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(options, { inputValue }) => {
                  // Allow custom values by including the input value if it doesn't exist in options
                  const filtered = options.filter(option =>
                    String(option)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  );

                  // Add the input value as an option if it's not empty and not already in the list
                  if (inputValue !== '' && !options.includes(inputValue)) {
                    filtered.push(inputValue);
                  }

                  return filtered;
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.listType || !formData.fieldName}
          >
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExclusionRulesGrid;
